/* eslint-disable no-console */
/**
 * Tab COMPRAS — tabla de documentos de costo (Fase 3b).
 *
 * Una fila por DOCUMENTO, de las tres fuentes unificadas por `documentosCosto.js`: facturas
 * SII recibidas (afectas / exentas / NC), facturas externas y boletas de honorarios.
 *
 * DOS EJES INDEPENDIENTES, uno por chip, y cada chip es también su filtro:
 *   · Clasificación (ER)  — Sin clasificar / [Cuenta] / Activo / Ignorada
 *   · Pago (partida abierta) — Impaga / Parcial X% / Pagada / Cubierta a cuenta / No aplica
 *
 * Ninguno de los dos sale de `estado_vinculacion` (regla 10.65): ese flag miente en el 21,8%
 * de los documentos de 2026. Ver el header de `documentosCosto.js`.
 *
 * Es una vista de LECTURA. La edición sigue yendo a los modales que ya existen — no se
 * reimplementa ninguno.
 */
import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { getFacturasConAsociacionCapex } from "../../utils/capex/asociarPagosCapex";
import {
  CLASIFICACION,
  PAGO,
  TIPO_DOCUMENTO,
  ORIGEN,
  ESTADO_SII,
  esPasivoFirme,
} from "../../utils/compras/documentosCosto";
import { useDocumentosCosto } from "../../utils/compras/useDocumentosCosto";
import { convertirAMonedaLocal } from "../../utils/exchangeRateService";
import { client } from "../../utils/supabase";
import AsociarPagosCapexModal from "../AsociarPagosCapexModal";
import AsignarRecibidaModal from "../ConciliacionSII/AsignarRecibidaModal";
import KebabMenu from "../KebabMenu";
import ClasificarHonorarioModal from "./ClasificarHonorarioModal";
import ClasificarModal, { ALCANCE } from "./ClasificarModal";
import styles from "./VentasFacturas.module.scss";

// ─── Helpers de formato ────────────────────────────────────

const formatMonto = (monto, moneda) => {
  if (monto == null) return "-";
  const n = Number(monto);
  if (moneda === "USD") return `US$ ${n.toLocaleString("es-CL", { maximumFractionDigits: 2 })}`;
  return `$ ${n.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

// Total de un documento expresado en CLP para las cards agregadas (TOTAL FACTURADO / CLASIFICADO),
// que sí mezclan orígenes y por eso NO pueden sumar crudo: convierte por el TC histórico de la
// FECHA de la factura reutilizando el util canónico `convertirAMonedaLocal` (reglas 10.19 / 10.27).
// Las tasas ya están en cache: `useDocumentosCosto` hace `await loadExchangeRates()` antes de
// exponer `documentos`.
//
// 10.177 (prohibido el fallo silencioso): `convertirAMonedaLocal` devuelve el número CRUDO para
// cualquier moneda que no sea CLP/UF/USD, y `getDolarValue` da 0 si el cache no cargó. Ambos casos
// producirían un total mentiroso, así que se DETECTAN acá y se devuelven como `problema` (la card los
// muestra como "⚠ revisar", nunca como un número). Devuelve `{ clp, problema }`.
const ES_LOCAL = (m) => m == null || m === "" || String(m).toUpperCase() === "CLP";
const monedaConvertible = (m, pais) => {
  const u = String(m || "").toUpperCase();
  if (u.includes("USD") || u.includes("US$") || u === "DÓLAR") return true;
  if (pais === "Chile" && u.includes("UF")) return true;
  return false; // EUR/PEN/otras: el util las pasaría crudas → hay que marcar, no sumar.
};
const totalDocEnCLP = (d, pais) => {
  const total = Number(d.total) || 0;
  if (ES_LOCAL(d.moneda)) return { clp: total, problema: null };
  if (!monedaConvertible(d.moneda, pais)) return { clp: 0, problema: `moneda no cubierta: ${d.moneda}` };
  const clp = convertirAMonedaLocal(total, d.moneda, pais, d.fecha);
  if (total && !clp) return { clp: 0, problema: `sin tipo de cambio para ${d.fecha}` };
  return { clp, problema: null };
};

// Label legible de una cuenta ER (no la key cruda). Usado por la celda Detalle para mostrar
// el carve-out (reparto de una factura entre 2 cuentas).
const labelCuenta = (v) => CUENTAS_ER.find((c) => c.value === v)?.label || v;

// Carve-out: 2 líneas apiladas con un divisor sutil entre ellas. La altura de línea es FIJA para
// que las divisorias queden a la MISMA altura en las 4 columnas (Neto/IVA/Total/Detalle) y se lean
// como una separación pareja. El divisor va como borderBottom de la 1ª línea (solo entre ambas).
const CARVE_LINEA_H = 22;
const CARVE_DIVISOR = "1px solid #e4e7e9";
const carveLineaStyle = (esPrimera, justify) => ({
  height: CARVE_LINEA_H,
  display: "flex",
  alignItems: "center",
  justifyContent: justify,
  ...(esPrimera ? { borderBottom: CARVE_DIVISOR } : {}),
});

// Carve-out: 2 líneas apiladas en una celda numérica (Neto/IVA/Total), alineadas a la derecha
// igual que el valor único, con el divisor entre ambas. Solo display.
const dosLineasNum = (v1, v2, moneda) => (
  <span style={{ display: "flex", flexDirection: "column" }}>
    <span style={carveLineaStyle(true, "flex-end")}>{formatMonto(v1, moneda)}</span>
    <span style={carveLineaStyle(false, "flex-end")}>{formatMonto(v2, moneda)}</span>
  </span>
);

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  const [, month, day] = fechaStr.substring(0, 10).split("-");
  return `${day}/${month}`;
};

const formatRut = (rut) => {
  if (!rut) return null;
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

// Formas jurídicas y siglas chilenas: se conservan con su casing canónico en vez del Title
// Case ingenuo (que daría "Spa"/"Ltda"). Clave = token en MAYÚSCULA; valor = forma correcta.
const SIGLAS = {
  SPA: "SpA",
  "S.P.A": "SpA",
  "S.P.A.": "SpA",
  LTDA: "Ltda",
  "LTDA.": "Ltda",
  SA: "SA",
  "S.A": "S.A.",
  "S.A.": "S.A.",
  EIRL: "EIRL",
  "E.I.R.L": "E.I.R.L.",
  "E.I.R.L.": "E.I.R.L.",
};

// Nombre del proveedor a Title Case, SOLO para display en la tabla (los datos en DB no se
// tocan). Cada palabra: 1ª mayúscula, resto minúscula. Excepciones: las siglas del mapa
// (SpA/Ltda/S.A./…) y cualquier token con punto interno (iniciales tipo "S.A.") se dejan
// como corresponde en vez de "Spa". Limitación: los acrónimos SIN punto (MMS, HDI, PCS) no
// se distinguen de una palabra corta común y caen en Title Case → "Mms", "Hdi".
const formatProveedor = (nombre) => {
  if (!nombre || nombre === "—") return nombre || "—";
  return nombre
    .trim()
    .split(/\s+/)
    .map((token) => {
      const up = token.toUpperCase();
      if (SIGLAS[up]) return SIGLAS[up];
      // Iniciales con punto (no listadas): se preservan en mayúscula.
      if (token.includes(".")) return up;
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(" ");
};

// ─── Chips ─────────────────────────────────────────────────
// Mismo formato de pastilla que ya usaba la tabla (padding 2/8, radio 10, 0.7rem, 600).

const chipStyle = (bg, color) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: "0.7rem",
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

// Chip de la columna Tipo: ancho uniforme para que la columna lea como una pila alineada
// (el más largo es "Nota Crédito"). Centrado, no envuelve.
const tipoChip = (bg, color) => ({ ...chipStyle(bg, color), minWidth: 92, textAlign: "center" });

const CHIP_TIPO = {
  [TIPO_DOCUMENTO.FACTURA]: tipoChip("#eaf2f8", "#2874a6"),
  [TIPO_DOCUMENTO.EXENTA]: tipoChip("#f4ecf7", "#7d3c98"),
  // La NC en rojo: es el único tipo que RESTA.
  [TIPO_DOCUMENTO.NOTA_CREDITO]: tipoChip("#fadbd8", "#a93226"),
  [TIPO_DOCUMENTO.HONORARIO]: tipoChip("#fef9e7", "#9a7d0a"),
  // Externa: antes gris sobre gris (bajo contraste). Slate más oscuro sobre gris claro.
  [TIPO_DOCUMENTO.EXTERNA]: tipoChip("#d6dbdf", "#2c3e50"),
};

// Label de display del chip. `TIPO_DOCUMENTO.NOTA_CREDITO` es "NC" (clave de datos/filtros),
// pero en la celda se muestra completo. El resto coincide con su value.
const LABEL_TIPO = { [TIPO_DOCUMENTO.NOTA_CREDITO]: "Nota Crédito" };

// Chip "NC {folio}" para marcar una factura anulada/ajustada por una NC (reciprocidad). Mismo
// rojo que el tipo "Nota Crédito", pero SIN el minWidth del chip de la columna Documento: el
// ancho lo da el texto, como el resto de los chips (chipStyle base).
const CHIP_NC_REF = chipStyle("#fadbd8", "#a93226");

const CHIP_CLASIFICACION = {
  [CLASIFICACION.SIN_CLASIFICAR]: chipStyle("#fdebd0", "#a66300"),
  [CLASIFICACION.CUENTA]: chipStyle("#d5f5e3", "#1e8449"),
  [CLASIFICACION.ACTIVO]: chipStyle("#d6eaf8", "#2874a6"),
  [CLASIFICACION.IGNORADA]: chipStyle("#e5e7e9", "#707b7c"),
};

// C2 — alerta ámbar (mismo lenguaje visual que "Sin clasificar"/"Pendiente") junto al chip Activo
// cuando el documento está clasificado Activo pero NO cuelga de ningún registro en `activos`.
const CHIP_ACTIVO_SIN_VINCULAR = chipStyle("#fdebd0", "#a66300");
// C4b — chip verde (mismo del gasto/cuenta) para un Activo correctamente vinculado.
const CHIP_ACTIVO_VINCULADO = chipStyle("#d5f5e3", "#1e8449");

// C4b — NATURALEZA (sub-columna izquierda): eje "gasto vs activo". Deriva del estado de la
// clasificación; el detalle (cuenta / vínculo) va en la sub-columna derecha.
const NATURALEZA_LABEL = {
  [CLASIFICACION.CUENTA]: "Gasto",
  [CLASIFICACION.ACTIVO]: "Activo",
  [CLASIFICACION.SIN_CLASIFICAR]: "Sin clasificar",
  [CLASIFICACION.IGNORADA]: "Ignorada",
};

const CHIP_PAGO = {
  [PAGO.IMPAGA]: chipStyle("#fadbd8", "#a93226"),
  [PAGO.PARCIAL]: chipStyle("#fdebd0", "#a66300"),
  [PAGO.PAGADA]: chipStyle("#d5f5e3", "#1e8449"),
  // Violeta: hay plata, falta la formalización del vínculo. Antes era turquesa (muy parecido
  // al verde de "Pagada" y se confundían); el violeta la separa claramente.
  [PAGO.CUBIERTA]: chipStyle("#e8daef", "#6c3483"),
  [PAGO.NO_APLICA]: chipStyle("#f4f6f6", "#909497"),
};

const TITULO_PAGO = {
  [PAGO.PAGADA]: "Pagada y formalizada: hay vínculo a movimiento bancario",
  [PAGO.PARCIAL]: "Vínculo bancario que no cubre el total del documento",
  [PAGO.CUBIERTA]: "El ledger la da por saldada con pagos a cuenta, SIN vínculo formal",
  [PAGO.IMPAGA]: "Sin vínculo bancario y con saldo vivo en el ledger",
  [PAGO.NO_APLICA]: "Una nota de crédito no se paga: reduce deuda",
};

// Eje estado_sii (ciclo de vida RCV). Aceptada en verde sobrio (estado normal, no distrae);
// Pendiente en ámbar (visible pero no es deuda firme); Rechazada en gris-rojo apagado.
const CHIP_ESTADO_SII = {
  [ESTADO_SII.ACEPTADA]: chipStyle("#eafaf1", "#1e8449"),
  [ESTADO_SII.PENDIENTE]: chipStyle("#fdebd0", "#a66300"),
  [ESTADO_SII.RECHAZADA]: chipStyle("#eceff1", "#78909c"),
};

const LABEL_ESTADO_SII = {
  [ESTADO_SII.ACEPTADA]: "Aceptada",
  // Chip corto; el detalle "pendiente por aprobar" vive en el tooltip (TITULO_ESTADO_SII).
  [ESTADO_SII.PENDIENTE]: "Pendiente",
  [ESTADO_SII.RECHAZADA]: "Rechazada",
};

const TITULO_ESTADO_SII = {
  [ESTADO_SII.ACEPTADA]: "En el RCV del SII — pasivo firme",
  [ESTADO_SII.PENDIENTE]: "En obligations/pending: aún sin acuse de recibo (Ley 19.983). NO suma al impago firme",
  [ESTADO_SII.RECHAZADA]: "Rechazada en el SII — no es deuda",
};

// El chip muestra solo la palabra del estado; el detalle (% amortizado, cuotas) vive en este
// tooltip para no perderlo. El detalle completo irá al modal de pagos en Fase 4.
const tooltipPago = (pago) => {
  const partes = [TITULO_PAGO[pago.estado]];
  if (pago.estado === PAGO.PARCIAL && pago.porcentaje) partes.push(`${pago.porcentaje}% amortizado`);
  if (pago.plan?.tieneCuotas) partes.push(`${pago.plan.cuotasPagadas}/${pago.plan.cuotas} cuotas`);
  return partes.filter(Boolean).join(" · ");
};

// El chip de Clasificación dispara el override per-factura SOLO para SII recibidas no-NC y no
// ignoradas: la NC (61) no se clasifica y la ignorada es un descarte contable, no una cuenta.
const esOverrideable = (d) =>
  d.origen === ORIGEN.SII &&
  d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO &&
  d.clasificacion.estado !== CLASIFICACION.IGNORADA;

// ─── Mini-modal: clasificar factura EXTERNA in-situ (Fase C, P2.44) ─────────
// Una externa se clasifica SOLO por su cuenta ER: `facturas_externas.cuenta_er` es la fuente
// ÚNICA (string, no FK; verificado: la leen ER/EECC/Compras en vivo, sin copia derivada) y
// `facturas_externas` no tiene `tipo_contable` → no hay path a Activo, solo Gasto. Este modal
// escribe SOLO ese campo (sin trazabilidad, sin PDF, sin proveedor) y recarga. El catálogo es
// el MISMO `CUENTAS_AGRUPADAS` (derivado de CUENTAS_ER) que usan ClasificarModal y el resto.
const ClasificarExternaModal = ({ doc, onClose, onAplicada }) => {
  const [cuenta, setCuenta] = useState(doc.cuentaEr || "");
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    if (!cuenta) return toast.error("Seleccioná una cuenta del Estado de Resultados");
    setGuardando(true);
    try {
      const { error } = await client.from("facturas_externas").update({ cuenta_er: cuenta }).eq("id", doc.id);
      if (error) throw error;
      toast.success("Factura externa clasificada");
      onAplicada();
    } catch (err) {
      toast.error("Error al clasificar: " + (err.message || ""));
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>Clasificar factura externa{doc.folio ? ` — ${doc.folio}` : ""}</ModalHeader>
      <ModalBody>
        <div style={{ fontSize: "0.82rem", marginBottom: 10, color: "#495057" }}>
          {doc.proveedorNombre} · {formatMonto(doc.total, doc.moneda)}
        </div>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuenta Estado de Resultados</div>
        <select
          value={cuenta}
          onChange={(e) => setCuenta(e.target.value)}
          style={{
            width: "100%",
            height: 36,
            fontSize: "0.82rem",
            padding: "4px 10px",
            border: "1px solid #ced4da",
            borderRadius: 4,
          }}
        >
          <option value="">— Seleccionar cuenta —</option>
          {CUENTAS_AGRUPADAS.map(({ grupo, cuentas }) => (
            <optgroup key={grupo} label={grupo}>
              {cuentas.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={guardar} disabled={guardando || !cuenta}>
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ─── Mini-modal: vincular una NOTA DE CRÉDITO (tipo_dte=61) al documento que anula/ajusta ──────
// Acción SEPARADA de la clasificación (Gasto/Activo): solo puebla el vínculo NC→documento en
// `facturas_sii_recibidas`. Selector 100% manual (sin preselección ni ranking por monto): lista
// plana de facturas del MISMO rut_emisor y MISMO país que la NC, ordenada por fecha. Excluye la
// propia NC y otras NC. Ambos campos obligatorios para guardar; se puede cerrar sin guardar (la NC
// queda sin vincular). Persiste documento_referenciado_id, tipo_relacion_nc, nc_vinculado_por
// (usuario real logueado, regla 10.104) y nc_vinculado_at. Se compara/guarda por ID (regla 10.7).
const VincularNotaCreditoModal = ({ doc, onClose, onAplicada }) => {
  const { id: userId } = useAuthContext();
  const [docRefId, setDocRefId] = useState(
    doc.raw?.documento_referenciado_id ? String(doc.raw.documento_referenciado_id) : ""
  );
  const [tipoRelacion, setTipoRelacion] = useState(doc.raw?.tipo_relacion_nc || "");
  const [candidatos, setCandidatos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  // La NC ya tiene un vínculo persistido → se ofrece Desvincular (y el form llega precargado).
  const yaVinculada = doc.raw?.documento_referenciado_id != null;

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!doc.proveedorRut) return;
      let q = client
        .from("facturas_sii_recibidas")
        .select("id, folio, tipo_dte, fecha_emision, monto_total")
        .eq("rut_emisor", doc.proveedorRut)
        .neq("id", doc.id)
        .neq("tipo_dte", 61)
        .order("fecha_emision", { ascending: true });
      if (doc.pais) q = q.eq("pais", doc.pais);
      const { data } = await q;
      if (vivo) setCandidatos(data || []);
    })();
    return () => {
      vivo = false;
    };
  }, [doc]);

  const selectStyle = (invalid) => ({
    width: "100%",
    height: 36,
    fontSize: "0.82rem",
    padding: "4px 10px",
    border: `1px solid ${invalid ? "#e74c3c" : "#ced4da"}`,
    borderRadius: 4,
  });

  const guardar = async () => {
    if (!docRefId || !tipoRelacion) return toast.error("Elegí el documento que anula/ajusta y el tipo de relación");

    // Validación de montos ANTES de guardar (10.177: sin fallo silencioso). Se compara el total
    // (valor absoluto) de la NC contra el total del documento referenciado:
    //   · anula_error      → deben ser EXACTAMENTE IGUALES (anulación total).
    //   · ajuste_comercial → la NC debe ser ESTRICTAMENTE MENOR (ajuste parcial).
    const ncTotal = Math.abs(Number(doc.total) || 0);
    const ref = candidatos.find((c) => String(c.id) === String(docRefId));
    if (!ref) {
      return toast.error(
        "No se pudo verificar el monto del documento referenciado (no está en la lista). No se guardó."
      );
    }
    const refTotal = Math.abs(Number(ref.monto_total) || 0);
    if (tipoRelacion === "anula_error" && ncTotal !== refTotal) {
      return toast.error(
        `El total de la NC (${formatMonto(ncTotal)}) debe ser IGUAL al del documento anulado ` +
          `(${formatMonto(refTotal)}). No coinciden — no se guardó.`
      );
    }
    if (tipoRelacion === "ajuste_comercial" && ncTotal >= refTotal) {
      return toast.error(
        `El total de la NC (${formatMonto(ncTotal)}) es igual o mayor al del documento ` +
          `(${formatMonto(refTotal)}). Si lo anula por completo, elegí "Anula factura — error, se reemitió". No se guardó.`
      );
    }

    // CANDADO anula_error (ER v2 Fase 3): no se puede ANULAR por NC una factura recibida que ya tiene
    // un pago bancario real vinculado — primero hay que desvincular el pago. Solo lectura, antes de
    // guardar. Solo aplica a 'anula_error' (ajuste_comercial no anula, sigue contando normal). Se
    // filtra factura_tipo='sii_recibida' (el factura_id colisiona entre tipos) y se ignoran los links
    // placeholder de monto 0 (no son pagos reales).
    if (tipoRelacion === "anula_error") {
      const { data: mbfRows, error: mbfErr } = await client
        .from("movimiento_bancario_facturas")
        .select("movimiento_bancario_id, monto")
        .eq("factura_tipo", "sii_recibida")
        .eq("factura_id", Number(docRefId));
      if (mbfErr) {
        return toast.error("No se pudo verificar si la factura tiene pagos vinculados. No se guardó.");
      }
      const pagosReales = (mbfRows || []).filter((r) => Number(r.monto) > 0);
      if (pagosReales.length > 0) {
        const detalle = pagosReales.map((p) => `mov ${p.movimiento_bancario_id} (${formatMonto(p.monto)})`).join(", ");
        return toast.error(
          `Esta factura tiene ${pagosReales.length} pago(s) bancario(s) vinculado(s): ${detalle}. ` +
            "Desvinculá el/los pago(s) antes de anularla por NC. No se guardó."
        );
      }
    }

    setGuardando(true);
    try {
      const { error } = await client
        .from("facturas_sii_recibidas")
        .update({
          documento_referenciado_id: Number(docRefId),
          tipo_relacion_nc: tipoRelacion,
          nc_vinculado_por: userId,
          nc_vinculado_at: new Date().toISOString(),
        })
        .eq("id", doc.id);
      if (error) throw error;
      toast.success("Nota de crédito vinculada");
      onAplicada();
    } catch (err) {
      toast.error("Error al vincular la NC: " + (err.message || ""));
      setGuardando(false);
    }
  };

  // Deshace el vínculo: limpia los 4 campos → la fila vuelve a "⚠ Sin vincular".
  const desvincular = async () => {
    setDesvinculando(true);
    try {
      const { error } = await client
        .from("facturas_sii_recibidas")
        .update({
          documento_referenciado_id: null,
          tipo_relacion_nc: null,
          nc_vinculado_por: null,
          nc_vinculado_at: null,
        })
        .eq("id", doc.id);
      if (error) throw error;
      toast.success("Vínculo de nota de crédito deshecho");
      onAplicada();
    } catch (err) {
      toast.error("Error al desvincular la NC: " + (err.message || ""));
      setDesvinculando(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>Vincular nota de crédito{doc.folio ? ` — Folio ${doc.folio}` : ""}</ModalHeader>
      <ModalBody>
        <div style={{ fontSize: "0.82rem", marginBottom: 12, color: "#495057" }}>
          {doc.proveedorNombre} · {formatMonto(doc.total)}
        </div>

        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
          Documento que anula o ajusta esta NC
        </div>
        <select value={docRefId} onChange={(e) => setDocRefId(e.target.value)} style={selectStyle(!docRefId)}>
          <option value="">— Seleccionar documento —</option>
          {candidatos.map((c) => (
            <option key={c.id} value={c.id}>
              {`Folio ${c.folio} · ${c.fecha_emision} · ${formatMonto(c.monto_total)}`}
            </option>
          ))}
        </select>
        {candidatos.length === 0 && (
          <div style={{ fontSize: "0.72rem", color: "#909497", marginTop: 4 }}>
            No hay facturas del mismo emisor y país para vincular.
          </div>
        )}

        <div style={{ fontSize: "0.78rem", fontWeight: 600, margin: "12px 0 4px" }}>Tipo de relación</div>
        <select
          value={tipoRelacion}
          onChange={(e) => setTipoRelacion(e.target.value)}
          style={selectStyle(!tipoRelacion)}
        >
          <option value="">— Seleccionar tipo —</option>
          <option value="anula_error">Anula factura — error, se reemitió</option>
          <option value="ajuste_comercial">Ajuste comercial — descuento, devolución real</option>
        </select>
      </ModalBody>
      <ModalFooter>
        {yaVinculada && (
          <Button
            color="danger"
            outline
            size="sm"
            onClick={desvincular}
            disabled={guardando || desvinculando}
            style={{ marginRight: "auto" }}
          >
            {desvinculando ? "Desvinculando..." : "Desvincular"}
          </Button>
        )}
        <Button color="secondary" size="sm" onClick={onClose} disabled={guardando || desvinculando}>
          Cancelar
        </Button>
        <Button
          color="primary"
          size="sm"
          onClick={guardar}
          disabled={guardando || desvinculando || !docRefId || !tipoRelacion}
        >
          {guardando ? "Guardando..." : yaVinculada ? "Actualizar" : "Vincular"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ─── Componente ────────────────────────────────────────────

const ComprasTab = ({ pais, filtros = {}, reloadNonce = 0 }) => {
  const { meses = [], clasificacion = "", tipo = "", pago = "", estadoSii = "", busqueda = "" } = filtros;
  const [modalFactura, setModalFactura] = useState(null);
  const [modalHonorario, setModalHonorario] = useState(null);
  // Modal ÚNICO de clasificación: { doc, alcance }. La FILA lo abre con alcance proveedor;
  // el CHIP, con alcance factura. Mismo componente, selector de alcance adentro.
  const [modalClasificar, setModalClasificar] = useState(null);
  // Mini-modal de clasificación de externa in-situ (Fase C): { doc }.
  const [modalExterna, setModalExterna] = useState(null);
  // Mini-modal de vínculo de NOTA DE CRÉDITO (tipo_dte=61) al documento que anula/ajusta: { doc }.
  const [modalVincularNC, setModalVincularNC] = useState(null);
  // Asociar pagos de CapEx ya hechos (adelantos por Inversión) a esta factura recibida (modal compartido).
  const [modalAsociarCapex, setModalAsociarCapex] = useState(null);
  // Set de factura_id con asociación CapEx ACTIVA → el kebab ofrece "Desasociar" (la factura ya quedó
  // Pagada por la asociación y sin esto no habría puerta al modo reversa). Se refresca con `documentos`.
  const [asociacionesCapex, setAsociacionesCapex] = useState(() => new Set());
  const { documentos, cargando, recargar } = useDocumentosCosto(pais);

  useEffect(() => {
    let vivo = true;
    getFacturasConAsociacionCapex(pais)
      .then((s) => vivo && setAsociacionesCapex(s))
      .catch(() => vivo && setAsociacionesCapex(new Set()));
    return () => {
      vivo = false;
    };
  }, [pais, documentos]);

  // `reloadNonce` lo mueve el botón "Sincronizar SII" del padre.
  const [ultimoNonce, setUltimoNonce] = useState(reloadNonce);
  if (reloadNonce !== ultimoNonce) {
    setUltimoNonce(reloadNonce);
    recargar();
  }

  // Reciprocidad NC→factura: para cada factura REFERENCIADA por una NC (documento_referenciado_id),
  // qué NC la anulan/ajustan. Se arma sobre TODOS los `documentos` (no `visibles`) para que el
  // indicador de la factura NO dependa de que su NC esté filtrada o no. Key = id de la factura
  // referenciada (facturas_sii_recibidas.id, comparado por ID — regla 10.7); valor = [{ folio, tipo }].
  const ncsPorFacturaRef = useMemo(() => {
    const map = new Map();
    (documentos || []).forEach((d) => {
      if (d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO) return;
      const refId = d.raw?.documento_referenciado_id;
      if (refId == null) return;
      if (!map.has(refId)) map.set(refId, []);
      map.get(refId).push({ folio: d.folio, tipo: d.raw?.tipo_relacion_nc || null });
    });
    return map;
  }, [documentos]);

  // Ids de facturas ANULADAS por completo (referenciadas por una NC con tipo 'anula_error'). Lo usa
  // el filtro "Facturas vigentes" para ocultarlas junto con las NC.
  const facturasAnuladas = useMemo(() => {
    const s = new Set();
    ncsPorFacturaRef.forEach((ncs, facturaId) => {
      if (ncs.some((n) => n.tipo === "anula_error")) s.add(facturaId);
    });
    return s;
  }, [ncsPorFacturaRef]);

  const visibles = useMemo(() => {
    let arr = documentos;
    if (meses.length > 0) arr = arr.filter((d) => d.fecha && meses.includes(d.fecha.substring(0, 7)));
    // "Facturas vigentes": oculta NC y facturas anuladas. Cualquier otro valor filtra por tipo exacto.
    if (tipo === "__vigentes__") {
      arr = arr.filter((d) => d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO && !facturasAnuladas.has(d.id));
    } else if (tipo) {
      arr = arr.filter((d) => d.tipo === tipo);
    }
    if (pago) arr = arr.filter((d) => d.pago.estado === pago);
    if (estadoSii) arr = arr.filter((d) => d.estadoSii === estadoSii);
    if (clasificacion === "__sin__") arr = arr.filter((d) => d.clasificacion.estado === CLASIFICACION.SIN_CLASIFICAR);
    else if (clasificacion === "__activo__") arr = arr.filter((d) => d.clasificacion.estado === CLASIFICACION.ACTIVO);
    else if (clasificacion === "__ignorada__")
      arr = arr.filter((d) => d.clasificacion.estado === CLASIFICACION.IGNORADA);
    else if (clasificacion) arr = arr.filter((d) => d.clasificacion.cuenta === clasificacion);
    if (busqueda.trim()) {
      const q = busqueda.trim();
      // C4a — término PURAMENTE numérico → folio EXACTO (buscar "89" trae el folio 89, no todo lo que
      // CONTENGA 89 como el folio 148553898) + RUT por dígitos (conserva la búsqueda por RUT numérico).
      // Texto (con letras) → substring por proveedor / razón social / RUT / folio, como siempre.
      if (/^\d+$/.test(q)) {
        arr = arr.filter((d) => String(d.folio || "") === q || (d.proveedorRut || "").replace(/\D/g, "").includes(q));
      } else {
        const ql = q.toLowerCase();
        arr = arr.filter(
          (d) =>
            (d.proveedorNombre || "").toLowerCase().includes(ql) ||
            (d.proveedorRut || "").toLowerCase().includes(ql) ||
            (d.folio || "").toLowerCase().includes(ql)
        );
      }
    }
    return arr;
  }, [documentos, meses, tipo, pago, clasificacion, estadoSii, busqueda, facturasAnuladas]);

  // Las cards reflejan EXACTAMENTE el mismo set filtrado que la tabla (`visibles`): reaccionan a
  // CUALQUIER filtro configurado (Meses, tipo, clasificación, pago, estado SII y búsqueda de
  // proveedor). Cada card conserva su recorte interno propio (sin-NC, sin-clasificar, impaga
  // firme, pendiente SII, IVA crédito, retención) aplicado sobre ese set filtrado.
  const resumen = useMemo(() => {
    const sinNC = visibles.filter((d) => d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO);
    const sinClasificar = sinNC.filter((d) => d.clasificacion.estado === CLASIFICACION.SIN_CLASIFICAR);
    // TOTAL FACTURADO / CLASIFICADO — un solo número en CLP, NETO de notas de crédito. Se calculan
    // sobre el set filtrado COMPLETO (`visibles`, incluida la NC) y cada doc entra con su `signo` (la
    // NC resta, signo -1), convertido a CLP por su fecha (totalDocEnCLP). La reconciliación
    // TOTAL = CLASIFICADO + (sin clasificar, neto) se cumple POR CONSTRUCCIÓN: `visibles` se parte en
    // {clasificado} ∪ {sin clasificar} usando el mismo `signo·clp` por documento. `problemaMoneda`
    // (10.177): si algún doc trae moneda no cubierta o sin TC, las dos cards muestran "⚠ revisar" en
    // vez de un número mentiroso. (Los otros 5 cards siguen sobre `sinNC`, sin tocar.)
    const visiblesConv = visibles.map((d) => {
      const { clp, problema } = totalDocEnCLP(d, pais);
      return { d, neto: d.signo * clp, problema };
    });
    const problemaMoneda = visiblesConv.find((x) => x.problema)?.problema || null;
    const totalFacturadoCLP = visiblesConv.reduce((s, x) => s + x.neto, 0);
    const clasificadoCLP = visiblesConv
      .filter((x) => x.d.clasificacion.estado !== CLASIFICACION.SIN_CLASIFICAR)
      .reduce((s, x) => s + x.neto, 0);
    // IMPAGO FIRME: documentos firmes impagos/parciales. Tras el override 2026-07-31 (regla 10.154)
    // 'pendiente' TAMBIÉN es firme, así que sus impagas suman acá; solo 'rechazada' queda fuera
    // (esPasivoFirme). Las cubiertas por débito/FIFO no entran (su pago.estado ya no es IMPAGA).
    const impagas = sinNC.filter(
      (d) => esPasivoFirme(d) && (d.pago.estado === PAGO.IMPAGA || d.pago.estado === PAGO.PARCIAL)
    );
    // Bucket INFORMATIVO de estado SII: compras que el SII aún no acusó. Tras el override 10.154 ya
    // NO implica exclusión del impago/por-pagar — es solo un corte de visibilidad del ciclo RCV.
    const pendientesAprob = sinNC.filter((d) => d.estadoSii === ESTADO_SII.PENDIENTE);
    return {
      totalFacturado: { monto: totalFacturadoCLP, problema: problemaMoneda },
      clasificado: { monto: clasificadoCLP, problema: problemaMoneda },
      sinClasificar: { n: sinClasificar.length, monto: sinClasificar.reduce((s, d) => s + d.total, 0) },
      impago: { n: impagas.length, monto: impagas.reduce((s, d) => s + (d.pago.saldo || d.total), 0) },
      pendientesAprob: {
        n: pendientesAprob.length,
        monto: pendientesAprob.reduce((s, d) => s + d.total, 0),
      },
      // IVA crédito: la NC RESTA (regla 10.42). Solo SII — las externas no generan crédito.
      iva: visibles.filter((d) => d.origen === ORIGEN.SII).reduce((s, d) => s + d.signo * d.iva, 0),
      // La retención NO es costo: es lo que la empresa entera al SII por las BHE.
      retencion: visibles.filter((d) => d.origen === ORIGEN.HONORARIO).reduce((s, d) => s + (d.retencion || 0), 0),
    };
  }, [visibles, pais]);

  const abrirEdicion = (doc) => {
    if (doc.origen === ORIGEN.SII) {
      // Con proveedor identificado → modal de clasificación con alcance PROVEEDOR por default
      // (propaga a sus recibidas 2026 sin clasificar; se puede cambiar a "solo esta factura").
      // Sin proveedor → AsignarRecibidaModal, que primero resuelve el proveedor.
      if (doc.proveedorId) setModalClasificar({ doc, alcance: ALCANCE.PROVEEDOR });
      else setModalFactura(doc);
    } else if (doc.origen === ORIGEN.HONORARIO) {
      setModalHonorario(doc);
    }
    // Externas todavía se clasifican desde EstadoCuenta (Fase C).
  };

  // Ítems del <KebabMenu> por fila (patrón 10.136). Fase 1: REPLICA exacta de los triggers
  // que hoy conviven — el onClick del <tr> (abrirEdicion, dispatch de :311-316) y el del chip
  // "Tipo" (override alcance FACTURA, gate esOverrideable). No relaja ni inventa gates: cada
  // ítem usa el MISMO handler y la MISMA condición que su trigger actual.
  const accionesDeFila = (d) => {
    const items = [];
    // Acción principal — mismo handler que abrirEdicion(d), solo cambia el label por estado.
    if (d.origen === ORIGEN.SII) {
      items.push({
        label: d.proveedorId ? "Clasificar proveedor" : "Asignar DTE",
        onClick: () => abrirEdicion(d),
      });
      // Solo NC (tipo_dte=61): acción DEDICADA para vincularla al documento que anula/ajusta. No
      // se mezcla con Clasificar (Gasto/Activo) — es otra cosa. Aparece únicamente en filas NC.
      if (d.tipo === TIPO_DOCUMENTO.NOTA_CREDITO) {
        items.push({
          label: "Vincular nota de crédito",
          onClick: () => setModalVincularNC({ doc: d }),
        });
      }
      // CapEx: mismo modal compartido, la puerta cambia según el estado (por ID, 10.7).
      //  · con asociación ACTIVA (factura ya Pagada por CapEx) → "Desasociar" (modo reversa del modal).
      //  · sin asociación e impaga/parcial → "Asociar" (listado de candidatos).
      // Sin este dos-caminos, una factura Pagada por CapEx no tendría cómo llegar al Desasociar
      // (el guard de Fase 2.5 redirige ahí, pero la puerta no existía → circular). Necesita proveedor_id.
      if (d.proveedorId) {
        if (asociacionesCapex.has(d.id)) {
          items.push({
            label: "Desasociar pagos de CapEx",
            onClick: () => setModalAsociarCapex({ facturaId: d.id, proveedorId: d.proveedorId }),
          });
        } else if (d.pago.estado === PAGO.IMPAGA || d.pago.estado === PAGO.PARCIAL) {
          items.push({
            label: "Asociar pagos de CapEx",
            onClick: () => setModalAsociarCapex({ facturaId: d.id, proveedorId: d.proveedorId }),
          });
        }
      }
    } else if (d.origen === ORIGEN.HONORARIO) {
      items.push({ label: "Clasificar honorario", onClick: () => abrirEdicion(d) });
    } else if (d.origen === ORIGEN.EXTERNA) {
      // Fase C (P2.44): la externa se clasifica IN-SITU con un mini-modal (antes navegaba a Estado
      // de Cuenta). Solo cuenta ER (facturas_externas.cuenta_er), sin proveedor → sirve también para
      // externas sin proveedorId. El verbo sale del MISMO campo que pinta los chips (d.clasificacion.estado).
      const sinClasificar = d.clasificacion.estado === CLASIFICACION.SIN_CLASIFICAR;
      items.push({
        label: sinClasificar ? "Clasificar" : "Editar clasificación",
        onClick: () => setModalExterna({ doc: d }),
      });
    }
    // Acción secundaria — mismo handler que el chip "Tipo" (:491), mismo gate.
    if (esOverrideable(d)) {
      items.push({
        label: "Clasificar solo esta factura",
        onClick: () => setModalClasificar({ doc: d, alcance: ALCANCE.FACTURA }),
      });
    }
    return items;
  };

  const cards = [
    {
      // Card principal: cuánto facturó el proveedor / el período del set filtrado, en CLP (convertido
      // por fecha). Informativa (azul, como IVA CRÉDITO), no alerta. "⚠ revisar" si hay moneda sin
      // convertir en el set (10.177) — nunca un número que mezcle unidades en silencio.
      label: "TOTAL FACTURADO",
      valor: resumen.totalFacturado.problema ? "⚠ revisar" : formatMonto(resumen.totalFacturado.monto),
      extra: resumen.totalFacturado.problema ? styles.cardAmber : styles.cardBlue,
      title: resumen.totalFacturado.problema || undefined,
    },
    {
      // Docs con clasificación del set filtrado, en CLP y NETO de NC (signo). Neutra (como RETENCIÓN).
      // Reconciliación: TOTAL FACTURADO = CLASIFICADO + (sin clasificar, neto y convertido).
      label: "CLASIFICADO",
      valor: resumen.clasificado.problema ? "⚠ revisar" : formatMonto(resumen.clasificado.monto),
      extra: resumen.clasificado.problema ? styles.cardAmber : "",
      title: resumen.clasificado.problema || undefined,
    },
    {
      label: `SIN CLASIFICAR (${resumen.sinClasificar.n})`,
      valor: formatMonto(resumen.sinClasificar.monto),
      // Ámbar (no bordó) para distinguirla de IMPAGO — que es la más crítica y se queda bordó.
      // Coincide con el color del chip "Sin clasificar" en la tabla.
      extra: resumen.sinClasificar.n > 0 ? styles.cardAmber : "",
    },
    {
      label: `IMPAGO (${resumen.impago.n})`,
      valor: formatMonto(resumen.impago.monto),
      extra: resumen.impago.n > 0 ? styles.cardRed : "",
    },
    {
      // Bucket informativo de estado SII (override 10.154): ya NO excluye del impago/por-pagar.
      label: `PEND. APROBAR (${resumen.pendientesAprob.n})`,
      valor: formatMonto(resumen.pendientesAprob.monto),
      extra: resumen.pendientesAprob.n > 0 ? styles.cardAmber : "",
    },
    { label: "IVA CRÉDITO", valor: formatMonto(resumen.iva), extra: styles.cardBlue },
    { label: "RETENCIÓN HON.", valor: formatMonto(resumen.retencion), extra: "" },
  ];

  return (
    <div className={styles.containerFacturasDetalle} style={{ paddingTop: 0 }}>
      <div className={styles.cardsSectionCompras}>
        {cards.map((c) => (
          <div key={c.label} className={`${styles.card} ${c.extra}`} title={c.title}>
            <div className={styles.cardLabel}>{c.label}</div>
            <div style={{ marginTop: 5 }}>
              <span className={styles.cardValue}>{c.valor}</span>
            </div>
          </div>
        ))}
      </div>

      {cargando ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>Cargando...</div>
      ) : visibles.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>No hay documentos con estos filtros</div>
      ) : (
        <div className={styles.tableResponsive}>
          <table className={styles.table}>
            {/* C4b — header agrupado (mismo patrón madre+hijas que la tabla de Empleados/RRHH):
                "Clasificación" es la columna MADRE con dos hijas (Naturaleza | Detalle). El resto
                de las columnas usan rowSpan={2}. La 2ª fila sticky va bajo la 1ª (ver .stickyHead). */}
            <thead className={styles.stickyHead}>
              <tr>
                <th rowSpan={2} className={styles.colCmpFecha}>
                  Fecha
                </th>
                <th rowSpan={2} className={styles.colCmpTipoDte} style={{ textAlign: "left" }}>
                  Documento
                </th>
                <th rowSpan={2} className={styles.colCmpProveedor} style={{ textAlign: "left" }}>
                  Proveedor
                </th>
                <th rowSpan={2} className={styles.colCmpRut}>
                  RUT
                </th>
                <th rowSpan={2} className={styles.colCmpFolio}>
                  Folio
                </th>
                <th rowSpan={2} className={styles.colCmpNeto} style={{ textAlign: "right" }}>
                  Neto
                </th>
                <th rowSpan={2} className={styles.colCmpIva} style={{ textAlign: "right" }}>
                  IVA
                </th>
                <th rowSpan={2} className={styles.colCmpTotal} style={{ textAlign: "right" }}>
                  Total
                </th>
                <th rowSpan={2} className={styles.colCmpEstadoSii} style={{ textAlign: "left" }}>
                  Estado SII
                </th>
                <th colSpan={2} style={{ textAlign: "center" }}>
                  Clasificación
                </th>
                <th rowSpan={2} className={styles.colCmpEstado} style={{ textAlign: "left" }}>
                  Pago
                </th>
                <th rowSpan={2} className={styles.colCmpAccion} style={{ textAlign: "center" }}>
                  Edit
                </th>
              </tr>
              <tr>
                <th className={styles.colCmpNaturaleza} style={{ textAlign: "left" }}>
                  Tipo
                </th>
                <th className={styles.colCmpDetalle} style={{ textAlign: "left" }}>
                  Detalle
                </th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((d) => {
                // Neto: si es 0 (exentas 34, honorarios) se muestra el exento — es el costo
                // real del documento. Mismo criterio que la tabla original.
                const netoMostrar = d.neto > 0 ? d.neto : d.exento;
                // Carve-out: la factura reparte su neto entre 2 cuentas ER (cuenta_er_2 +
                // monto_er_2). Solo display en Neto/IVA/Total/Detalle; motor/persistencia no se tocan.
                const tieneCarveOut = Boolean(d.cuentaEr2) && Number(d.montoEr2) > 0;
                // Reciprocidad: NC (de otra fila) que anulan/ajustan ESTA factura. Solo aplica a
                // facturas SII no-NC (una NC no se referencia a sí misma ni a otra NC).
                const ncsQueLaReferencian =
                  d.origen === ORIGEN.SII && d.tipo !== TIPO_DOCUMENTO.NOTA_CREDITO
                    ? ncsPorFacturaRef.get(d.id) || null
                    : null;
                // Anulada por completo: existe una NC que la referencia con tipo 'anula_error' (validado:
                // total NC == total factura). La fila se pinta como anulada — SOLO visual en esta tabla:
                // tipo_contable / cuenta_er / vínculo de pago en la base NO se tocan.
                const ncAnula = ncsQueLaReferencian?.find((n) => n.tipo === "anula_error") || null;
                const chipAnulada = ncAnula ? (
                  <span style={CHIP_NC_REF} title={`Anulada por NC ${ncAnula.folio} (error, se reemitió)`}>
                    {`NC ${ncAnula.folio}`}
                  </span>
                ) : null;
                // La propia NC (tipo_dte=61): sus columnas Tipo, Detalle y Pago muestran "No aplica"
                // (una NC no es Gasto/Activo ni tiene cuenta/pago propios). Estado SII NO cambia.
                const esNC = d.tipo === TIPO_DOCUMENTO.NOTA_CREDITO;
                const chipNoAplica = <span style={CHIP_PAGO[PAGO.NO_APLICA]}>{PAGO.NO_APLICA}</span>;
                // Split de neto/IVA/total en 2 líneas. La 2ª línea toma el REMAINDER del IVA para
                // que la suma cuadre EXACTO con la factura real (sin drift por redondeo).
                const carveNeto2 = Number(d.montoEr2) || 0;
                const carveNeto1 = netoMostrar - carveNeto2;
                const ivaFactura = Number(d.iva) || 0;
                const carveIva1 = netoMostrar ? Math.round((ivaFactura * carveNeto1) / netoMostrar) : 0;
                const carveIva2 = ivaFactura - carveIva1;
                const carveTotal1 = carveNeto1 + carveIva1;
                const carveTotal2 = carveNeto2 + carveIva2;
                return (
                  <tr key={d.clave}>
                    <td className={styles.colCmpFecha} style={{ textAlign: "center" }}>
                      {formatFecha(d.fecha)}
                    </td>
                    <td className={styles.colCmpTipoDte}>
                      <span style={CHIP_TIPO[d.tipo]}>{LABEL_TIPO[d.tipo] || d.tipo}</span>
                    </td>
                    <td
                      className={styles.colCmpProveedor}
                      title={
                        d.razonSocial && d.razonSocial !== d.proveedorNombre
                          ? `Razón social SII: ${d.razonSocial}`
                          : undefined
                      }
                    >
                      {formatProveedor(d.proveedorNombre)}
                    </td>
                    <td className={styles.colCmpRut} style={{ textAlign: "center" }}>
                      {formatRut(d.proveedorRut) || "—"}
                    </td>
                    <td className={styles.colCmpFolio} style={{ textAlign: "center" }}>
                      {d.folio || "—"}
                    </td>
                    <td className={styles.colCmpNeto} style={{ textAlign: "right" }}>
                      {tieneCarveOut
                        ? dosLineasNum(carveNeto1, carveNeto2, d.moneda)
                        : formatMonto(netoMostrar, d.moneda)}
                    </td>
                    <td className={styles.colCmpIva} style={{ textAlign: "right" }}>
                      {tieneCarveOut ? dosLineasNum(carveIva1, carveIva2, d.moneda) : formatMonto(d.iva, d.moneda)}
                    </td>
                    <td className={styles.colCmpTotal} style={{ textAlign: "right", fontWeight: 600 }}>
                      {tieneCarveOut
                        ? dosLineasNum(carveTotal1, carveTotal2, d.moneda)
                        : d.signo < 0
                          ? `− ${formatMonto(d.total, d.moneda)}`
                          : formatMonto(d.total, d.moneda)}
                    </td>
                    <td className={styles.colCmpEstadoSii}>
                      {/* Estado SII = ciclo de vida real del RCV (Aceptada/Pendiente/Rechazada), SIEMPRE.
                          Nunca se sobrescribe — ni en la factura anulada ni en la propia NC. Solo aplica
                          a documentos SII; externas/honorarios muestran "—". */}
                      {d.origen === ORIGEN.SII ? (
                        <span style={CHIP_ESTADO_SII[d.estadoSii]} title={TITULO_ESTADO_SII[d.estadoSii]}>
                          {LABEL_ESTADO_SII[d.estadoSii]}
                        </span>
                      ) : (
                        <span style={{ color: "#bbb" }}>—</span>
                      )}
                    </td>
                    {/* C4b — NATURALEZA (gasto/activo/sin clasificar). Fase 2: chip de DISPLAY puro.
                        La acción de clasificar (proveedor / solo esta factura) vive ahora SOLO en el
                        kebab — la fila y el chip dejaron de ser triggers. El detalle (cuenta / vínculo)
                        va en la celda de al lado. */}
                    <td className={styles.colCmpNaturaleza}>
                      {/* Anulada por NC → "NC {folio}" rojo. Propia NC → "No aplica" gris. Resto → naturaleza. */}
                      {ncAnula ? (
                        chipAnulada
                      ) : esNC ? (
                        chipNoAplica
                      ) : (
                        <span style={CHIP_CLASIFICACION[d.clasificacion.estado]}>
                          {NATURALEZA_LABEL[d.clasificacion.estado] || d.clasificacion.label}
                        </span>
                      )}
                    </td>
                    {/* C4b — DETALLE según naturaleza: Gasto → cuenta ER (texto); Activo → Vinculado /
                        ⚠ Sin vincular (eje C2); Sin clasificar / Ignorada → vacío. */}
                    <td className={styles.colCmpDetalle}>
                      {/* Anulada por NC → "NC {folio}" rojo. Propia NC → "No aplica" gris. Resto →
                          cuenta/activo + (si la referencia una NC) el chip de reciprocidad. */}
                      {ncAnula ? (
                        chipAnulada
                      ) : esNC ? (
                        chipNoAplica
                      ) : (
                        <span style={{ display: "inline-flex", flexDirection: "column", gap: 3, alignItems: "center" }}>
                          {d.clasificacion.estado === CLASIFICACION.CUENTA ? (
                            tieneCarveOut ? (
                              // Carve-out: 2 chips apilados, solo el label de cada cuenta. Los montos
                              // (neto/IVA/total por cuenta) viven en las columnas Neto/IVA/Total, alineados
                              // a estas 2 líneas. Principal = cuenta_er; secundaria = cuenta_er_2.
                              <span style={{ display: "flex", flexDirection: "column" }}>
                                <span style={carveLineaStyle(true, "center")}>
                                  <span
                                    style={CHIP_CLASIFICACION[CLASIFICACION.CUENTA]}
                                    title={labelCuenta(d.cuentaEr)}
                                  >
                                    {labelCuenta(d.cuentaEr)}
                                  </span>
                                </span>
                                <span style={carveLineaStyle(false, "center")}>
                                  <span
                                    style={CHIP_CLASIFICACION[CLASIFICACION.CUENTA]}
                                    title={labelCuenta(d.cuentaEr2)}
                                  >
                                    {labelCuenta(d.cuentaEr2)}
                                  </span>
                                </span>
                              </span>
                            ) : (
                              // C4c — la cuenta ER se muestra como chip de color (mismo verde que el chip
                              // "Gasto" hermano y que la pastilla pre-C4b), no como texto plano.
                              <span style={CHIP_CLASIFICACION[CLASIFICACION.CUENTA]} title={d.clasificacion.label}>
                                {d.clasificacion.label}
                              </span>
                            )
                          ) : d.clasificacion.estado === CLASIFICACION.ACTIVO ? (
                            d.activoVinculado ? (
                              <span style={CHIP_ACTIVO_VINCULADO}>Vinculado</span>
                            ) : (
                              <span
                                style={CHIP_ACTIVO_SIN_VINCULAR}
                                title="Clasificado Activo pero no cuelga de ningún registro en Activos"
                              >
                                ⚠ Sin vincular
                              </span>
                            )
                          ) : null}
                          {/* Reciprocidad: si a ESTA factura una NC la anula/ajusta, se marca con el
                            MISMO rojo del tipo "Nota Crédito" (ancho del texto). El folio de la NC va
                            en el texto; anula vs ajuste, en el title. */}
                          {ncsQueLaReferencian && ncsQueLaReferencian.length > 0 && (
                            <span
                              style={CHIP_NC_REF}
                              title={ncsQueLaReferencian
                                .map((n) => {
                                  const rel =
                                    n.tipo === "anula_error"
                                      ? "anula (error)"
                                      : n.tipo === "ajuste_comercial"
                                        ? "ajuste comercial"
                                        : "vinculada";
                                  return `NC ${n.folio} — ${rel}`;
                                })
                                .join(" · ")}
                            >
                              {ncsQueLaReferencian.length === 1
                                ? `NC ${ncsQueLaReferencian[0].folio}`
                                : `NC ×${ncsQueLaReferencian.length}`}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className={styles.colCmpEstado}>
                      {/* Anulada por NC → "No aplica" en gris (mismo estilo que la fila de la propia NC):
                          un documento anulado no puede aparecer con un pago vigente. Solo visual: el
                          vínculo de pago real en la base NO se toca. */}
                      {ncAnula ? (
                        <span style={CHIP_PAGO[PAGO.NO_APLICA]} title="Documento anulado por NC: no corresponde pago">
                          {PAGO.NO_APLICA}
                        </span>
                      ) : (
                        <span style={CHIP_PAGO[d.pago.estado]} title={tooltipPago(d.pago)}>
                          {d.pago.estado}
                        </span>
                      )}
                    </td>
                    {/* Acciones por fila — vía ÚNICA (Fase 2). El <tr> ya no tiene onClick, así que
                        no hace falta stopPropagation acá. */}
                    <td className={styles.colCmpAccion} style={{ textAlign: "center" }}>
                      <KebabMenu items={accionesDeFila(d)} minWidth={180} buttonClassName={styles.kebabAccion} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalFactura && (
        <AsignarRecibidaModal
          isOpen
          toggle={() => setModalFactura(null)}
          factura={modalFactura.raw}
          onAsignada={() => {
            setModalFactura(null);
            recargar();
          }}
        />
      )}

      {modalHonorario && (
        <ClasificarHonorarioModal
          boleta={modalHonorario.raw}
          onClose={() => setModalHonorario(null)}
          onClasificada={() => {
            setModalHonorario(null);
            recargar();
          }}
        />
      )}

      {modalClasificar && (
        <ClasificarModal
          doc={modalClasificar.doc}
          alcanceInicial={modalClasificar.alcance}
          onClose={() => setModalClasificar(null)}
          onAplicada={() => {
            setModalClasificar(null);
            recargar();
          }}
        />
      )}

      {modalExterna && (
        <ClasificarExternaModal
          doc={modalExterna.doc}
          onClose={() => setModalExterna(null)}
          onAplicada={() => {
            setModalExterna(null);
            recargar();
          }}
        />
      )}

      {modalVincularNC && (
        <VincularNotaCreditoModal
          doc={modalVincularNC.doc}
          onClose={() => setModalVincularNC(null)}
          onAplicada={() => {
            setModalVincularNC(null);
            recargar();
          }}
        />
      )}

      {modalAsociarCapex && (
        <AsociarPagosCapexModal
          show
          facturaId={modalAsociarCapex.facturaId}
          proveedorId={modalAsociarCapex.proveedorId}
          pais={pais}
          onClose={() => setModalAsociarCapex(null)}
          onSuccess={recargar}
        />
      )}
    </div>
  );
};

/** Opciones del filtro de Clasificación — el mismo eje que el chip. */
export const OPCIONES_CLASIFICACION = [
  { value: "", label: "Toda clasificación" },
  { value: "__sin__", label: "Sin clasificar" },
  { value: "__activo__", label: "Activo" },
  { value: "__ignorada__", label: "Ignorada" },
];

/** Opciones del filtro de Pago — el mismo eje que el chip. */
export const OPCIONES_PAGO = [
  { value: "", label: "Todo estado de pago" },
  { value: PAGO.IMPAGA, label: "Pendiente de pago" },
  { value: PAGO.PARCIAL, label: "Parcial" },
  { value: PAGO.PAGADA, label: "Pagada (formal)" },
  { value: PAGO.CUBIERTA, label: "Cubierta a cuenta" },
  { value: PAGO.NO_APLICA, label: "No aplica (NC)" },
];

/** Opciones del filtro de Estado SII (ciclo de vida RCV) — mismo eje que el chip "Estado". */
export const OPCIONES_ESTADO_SII = [
  { value: "", label: "Todo estado SII" },
  { value: ESTADO_SII.ACEPTADA, label: "Aceptada" },
  { value: ESTADO_SII.PENDIENTE, label: "Pendiente por aprobar" },
  { value: ESTADO_SII.RECHAZADA, label: "Rechazada" },
];

/** Opciones del filtro de Tipo — la NC aislada, que es el pedido explícito. */
export const OPCIONES_TIPO = [
  { value: "", label: "Todo tipo" },
  // Vista "limpia": oculta las NC y las facturas anuladas por una NC (anula_error). Valor especial
  // (no es un TIPO_DOCUMENTO), resuelto en el filtro de `visibles`.
  { value: "__vigentes__", label: "Facturas vigentes" },
  { value: TIPO_DOCUMENTO.FACTURA, label: "Factura afecta" },
  { value: TIPO_DOCUMENTO.EXENTA, label: "Exenta" },
  { value: TIPO_DOCUMENTO.NOTA_CREDITO, label: "Nota de crédito" },
  { value: TIPO_DOCUMENTO.HONORARIO, label: "Honorario" },
  { value: TIPO_DOCUMENTO.EXTERNA, label: "Externa" },
];

export const CUENTAS_AGRUPADAS = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));

export default ComprasTab;
